import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type {
  BrandModelFormData,
  ICategory,
  IBrandModel,
  PaginatedResponse,
} from "../../../types/equipment";
import {
  AddCircleOutline,
  Close,
  ExpandMore,
  ChevronRight,
} from "@mui/icons-material";

interface BrandModelStepProps {
  formData: BrandModelFormData;
  onChange: (field: keyof BrandModelFormData, value: string) => void;
  onNext: () => void;
  onSave: () => void;
  isEdit: boolean;
}

export default function BrandModelStep({
  formData,
  onChange,
  onNext,
  onSave,
}: BrandModelStepProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [brandModels, setBrandModels] = useState<IBrandModel[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedBrandModel, setSelectedBrandModel] =
    useState<IBrandModel | null>(null);
  const [showSpecs, setShowSpecs] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", parent: "" });
  const [savingCategory, setSavingCategory] = useState(false);

  const isExistingSelected = !!formData.brand_model_id;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[400],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  const errorSx = {
    fontSize: "0.75rem",
    color: "error.main",
    mt: 0.5,
  };

  useEffect(() => {
    apiService
      .get<PaginatedResponse<ICategory>>("/api/equipments/categories/")
      .then((res) => setCategories(res.data.results))
      .catch(() => {});
  }, []);

  const handleSaveCategory = async () => {
    setSavingCategory(true);
    try {
      const res = await apiService.post<ICategory>(
        "/api/equipments/categories/",
        {
          name: newCategory.name,
          ...(newCategory.parent ? { parent: newCategory.parent } : {}),
        },
      );
      const saved = res.data;
      setCategories((prev) => [...prev, saved]);
      onChange("category_id", saved.id.toString());
      setAddCategoryOpen(false);
      setNewCategory({ name: "", parent: "" });
    } catch (e) {
      console.error(e);
    } finally {
      setSavingCategory(false);
    }
  };

  const handleBrandModelSearch = async (query: string) => {
    if (!query || query.length < 2) {
      setBrandModels([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await apiService.get<PaginatedResponse<IBrandModel>>(
        `/api/equipments/brand-model/?search=${encodeURIComponent(query)}`,
      );
      setBrandModels(res.data.results);
    } catch {
      setBrandModels([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectBrandModel = (bm: IBrandModel | null) => {
    setSelectedBrandModel(bm);
    if (bm) {
      onChange("brand_model_id", bm.id.toString());
      onChange("brand", bm.brand_name || "");
      onChange("model", bm.model || "");
      onChange("manufacturer", bm.manufacturer || "");
      onChange("rental_value", bm.rental_value || "");
      onChange("length", bm.length || "");
      onChange("height", bm.height || "");
      onChange("width", bm.width || "");
      onChange("weight", bm.weight || "");
      onChange("power_draw", bm.power_draw || "");
      onChange("current_draw", bm.current_draw || "");
      onChange("category_id", bm.category?.id.toString() || "");
    } else {
      onChange("brand_model_id", "");
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!isExistingSelected) {
      if (!formData.brand.trim()) newErrors.brand = "Brand is required";
      if (!formData.model.trim()) newErrors.model = "Model is required";
      if (!formData.category_id) newErrors.category_id = "Category is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Search existing */}
      <Box>
        <Typography variant="body2" sx={labelSx}>
          Search Existing Model
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Select to add a new unit to an existing catalog entry, or leave blank
          to create a new one.
        </Typography>
        <Autocomplete
          options={brandModels}
          loading={searchLoading}
          value={selectedBrandModel}
          onChange={(_, val) => handleSelectBrandModel(val)}
          onInputChange={(_, val) => handleBrandModelSearch(val)}
          getOptionLabel={(opt) =>
            `${opt.brand_name || ""} ${opt.model || ""}`.trim() ||
            `Model #${opt.id}`
          }
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderOption={(props, opt) => (
            <li {...props} key={opt.id}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {opt.brand_name} {opt.model}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.category?.name} · {opt.brand_model_quantity ?? 0}{" "}
                  available
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Type brand or model name..."
              size="small"
              sx={inputSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center" }}
      >
        — or define a new catalog entry —
      </Typography>

      {/* Brand + Model */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Brand *
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. Sony"
            size="small"
            value={formData.brand}
            onChange={(e) => {
              onChange("brand", e.target.value);
              setErrors((p) => ({ ...p, brand: "" }));
            }}
            disabled={isExistingSelected}
            error={!!errors.brand}
            sx={inputSx}
          />
          {errors.brand && <Typography sx={errorSx}>{errors.brand}</Typography>}
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Model *
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. FX6"
            size="small"
            value={formData.model}
            onChange={(e) => {
              onChange("model", e.target.value);
              setErrors((p) => ({ ...p, model: "" }));
            }}
            disabled={isExistingSelected}
            error={!!errors.model}
            sx={inputSx}
          />
          {errors.model && <Typography sx={errorSx}>{errors.model}</Typography>}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Category *
          </Typography>
          <FormControl
            fullWidth
            size="small"
            sx={inputSx}
            disabled={isExistingSelected}
            error={!!errors.category_id}
          >
            <Select
              displayEmpty
              value={formData.category_id}
              onChange={(e) => {
                onChange("category_id", e.target.value);
                setErrors((p) => ({ ...p, category_id: "" }));
              }}
              renderValue={(v) =>
                v ? (
                  (categories.find((c) => c.id.toString() === v)?.name ?? v)
                ) : (
                  <Typography sx={{ color: "#A0938D" }}>
                    Select category
                  </Typography>
                )
              }
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id.toString()}>
                  {c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}
                </MenuItem>
              ))}
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setAddCategoryOpen(true);
                }}
                sx={{
                  borderTop: `1px solid ${colors.primary[700]}`,
                  mt: 0.5,
                  pt: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: colors.primary[400],
                  }}
                >
                  <AddCircleOutline sx={{ fontSize: 16 }} />
                  <Typography fontSize={13} fontWeight={500}>
                    Add New Category
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          {errors.category_id && (
            <Typography sx={errorSx}>{errors.category_id}</Typography>
          )}
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>
            Rental Value / day
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. 50"
            size="small"
            type="number"
            value={formData.rental_value}
            onChange={(e) => onChange("rental_value", e.target.value)}
            disabled={isExistingSelected}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* Description */}
      <Box>
        <Typography variant="body2" sx={labelSx}>
          Description
        </Typography>
        <TextField
          fullWidth
          placeholder="Optional notes about this model type..."
          size="small"
          multiline
          rows={2}
          value={formData.description}
          onChange={(e) => onChange("description", e.target.value)}
          disabled={isExistingSelected}
          sx={inputSx}
        />
      </Box>

      <Box>
        <Box
          onClick={() => setShowSpecs((p) => !p)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            userSelect: "none",
            color: colors.primary[400],
          }}
        >
          {showSpecs ? (
            <ExpandMore sx={{ fontSize: 18 }} />
          ) : (
            <ChevronRight sx={{ fontSize: 18 }} />
          )}
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 600,
              color: colors.primary[400],
            }}
          >
            Technical specs (dimensions, power draw)
          </Typography>
        </Box>

        <Collapse in={showSpecs}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {/* Dimensions */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: 2,
              }}
            >
              {(["length", "height", "width", "weight"] as const).map(
                (field) => (
                  <Box key={field}>
                    <Typography
                      variant="body2"
                      sx={labelSx}
                      textTransform="capitalize"
                    >
                      {field}
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder={field === "weight" ? "kg" : "cm"}
                      size="small"
                      type="number"
                      value={formData[field]}
                      onChange={(e) => onChange(field, e.target.value)}
                      disabled={isExistingSelected}
                      sx={inputSx}
                    />
                  </Box>
                ),
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  Power Draw
                </Typography>
                <TextField
                  fullWidth
                  placeholder="W"
                  size="small"
                  value={formData.power_draw}
                  onChange={(e) => onChange("power_draw", e.target.value)}
                  disabled={isExistingSelected}
                  sx={inputSx}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  Current Draw
                </Typography>
                <TextField
                  fullWidth
                  placeholder="A"
                  size="small"
                  value={formData.current_draw}
                  onChange={(e) => onChange("current_draw", e.target.value)}
                  disabled={isExistingSelected}
                  sx={inputSx}
                />
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Nav */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography
          onClick={onSave}
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            textDecoration: "underline",
            color: colors.primary[400],
            fontSize: "0.9rem",
          }}
        >
          Save Gear
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          sx={{
            backgroundColor: colors.primary[400],
            px: 5,
            py: 1.2,
            borderRadius: "24px",
          }}
        >
          Next
        </Button>
      </Box>

      <Dialog
        open={addCategoryOpen}
        onClose={() => setAddCategoryOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, backgroundColor: colors.primary[900] },
        }}
      >
        <IconButton
          onClick={() => setAddCategoryOpen(false)}
          sx={{ position: "absolute", right: 14, top: 14 }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
        <DialogContent sx={{ p: "24px" }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color={colors.primary[400]}
            mb={3}
          >
            Add New Category
          </Typography>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Category Name *
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Cameras"
              value={newCategory.name}
              onChange={(e) =>
                setNewCategory((p) => ({ ...p, name: e.target.value }))
              }
              size="small"
              sx={inputSx}
            />
          </Box>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Parent Category (optional)
            </Typography>
            <FormControl fullWidth size="small" sx={inputSx}>
              <Select
                displayEmpty
                value={newCategory.parent}
                onChange={(e) =>
                  setNewCategory((p) => ({ ...p, parent: e.target.value }))
                }
                renderValue={(v) =>
                  v ? (
                    (categories.find((c) => c.id.toString() === v)?.name ?? v)
                  ) : (
                    <Typography sx={{ color: "#A0938D" }}>
                      None (top level)
                    </Typography>
                  )
                }
              >
                <MenuItem value="">None (top level)</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={handleSaveCategory}
              disabled={savingCategory || !newCategory.name.trim()}
              sx={{
                backgroundColor: colors.primary[400],
                color: colors.primary[900],
                borderRadius: "12px",
                px: 4,
                "&.Mui-disabled": { backgroundColor: colors.primary[800] },
              }}
            >
              {savingCategory ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Save Category"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
